<template>
    <v-container fluid>
        <!-- Edit Product Dialog -->
        <ProductEditDialog
            :dialog.sync="editProductDialog"
            :object.sync="product"
        ></ProductEditDialog>

        <!-- Product Cost Template Dialog -->
        <ProductFixedCostTemplateEditDialog
            :dialog.sync="editProductFixedCostTemplateDialog"
            v-on:item-created="onProductFixedCostTemplateCreated"
        ></ProductFixedCostTemplateEditDialog>

        <!-- Delete Product Dialog -->
        <DeleteDialog
            :dialog.sync="deleteProductDialog"
            :item="product"
            v-on:confirmed="deleteProductConfirmed"
        >
            <template v-slot:title>Delete - {{ product.name }}</template>
            <template v-slot:content
                >Are you sure you want to delete this product?</template
            >
        </DeleteDialog>

        <v-row class="mx-0">
            <!-- Product Detail Card-->
            <v-col cols="12" sm="5" md="4" lg="3" class="px-2">
                <ProductInfoCard
                    :product="product"
                    :loading="$apollo.queries.product.loading"
                >
                    <template v-slot:header>
                        <v-card-title>
                            <v-icon color="primary" class="mr-2"
                                >local_offer</v-icon
                            >Product
                            <v-chip
                                class="ml-2"
                                small
                                color="primary"
                                v-if="product.isDynamic"
                                >Dynamic</v-chip
                            >
                            <v-spacer></v-spacer>
                            <v-btn
                                v-if="hasPermission('change_product')"
                                class="mx-2"
                                color="primary"
                                outlined
                                dark
                                fab
                                x-small
                                @click="editProductDialog = true"
                            >
                                <v-icon>edit</v-icon>
                            </v-btn>
                            <v-btn
                                v-if="hasPermission('delete_product')"
                                color="error"
                                outlined
                                dark
                                fab
                                x-small
                                @click="deleteProductDialog = true"
                            >
                                <v-icon>delete</v-icon>
                            </v-btn>
                        </v-card-title>
                    </template>
                </ProductInfoCard>

                <!-- product cost -->
                <v-hover>
                    <template v-slot="{ hover }">
                        <v-card :elevation="hover ? 6 : 3" class="mt-3">
                            <v-card-title>
                                <v-icon color="primary" class="mr-2"
                                    >euro_symbol</v-icon
                                >
                                Product Cost
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }"
                                        ><v-icon
                                            class="ml-1"
                                            small
                                            color="grey"
                                            v-on="on"
                                            >error_outline</v-icon
                                        >
                                    </template>
                                    <span>Total product fixed cost</span>
                                </v-tooltip>
                                <v-spacer></v-spacer>
                                €{{ productTotalCost }}
                            </v-card-title>

                            <v-row class="mx-0">
                                <v-col cols="12" class="px-3">
                                    <!-- fixed cost template -->
                                    <v-autocomplete
                                        hide-details
                                        v-model="productFixedCostTemplateNodeId"
                                        :items="
                                            tenantProductFixedCostTemplates
                                                ? tenantProductFixedCostTemplates
                                                : []
                                        "
                                        item-text="node.templateName"
                                        item-value="node.id"
                                        :loading="
                                            $apollo.queries
                                                .tenantProductFixedCostTemplates
                                                .loading ||
                                                isLoadingTenantProductFixedCostTemplates
                                        "
                                        label="Select Product Cost Template"
                                        class="subtitle-2"
                                        :change="linkProductFixedCostTemplate()"
                                        outlined
                                    >
                                        <template
                                            slot="item"
                                            slot-scope="{ item }"
                                        >
                                            {{ item.node.templateName }}
                                            <v-spacer></v-spacer>
                                            <span class="ml-2 grey--text">
                                                €{{
                                                    item.node.totalCost.toFixed(
                                                        3
                                                    )
                                                }}
                                            </span>
                                        </template>
                                        <template
                                            slot="selection"
                                            slot-scope="{ item }"
                                        >
                                            {{ item.node.templateName }}
                                            <v-spacer></v-spacer>
                                            <span class="ml-2 grey--text">
                                                €{{
                                                    item.node.totalCost.toFixed(
                                                        3
                                                    )
                                                }}
                                            </span>
                                        </template>
                                        <template
                                            v-if="
                                                hasPermission('change_product')
                                            "
                                            v-slot:append-outer
                                        >
                                            <v-btn
                                                text
                                                small
                                                tabindex="-1"
                                                @click="
                                                    addProductFixedCostTemplate()
                                                "
                                            >
                                                <v-icon
                                                    color="grey"
                                                    tabindex="-1"
                                                    >add</v-icon
                                                >
                                            </v-btn>
                                        </template>
                                    </v-autocomplete>

                                    <v-data-table
                                        :headers="
                                            productFixedCostTemplateItemTableHeaders
                                        "
                                        :items="
                                            product.productFixedCostTemplate &&
                                            product.productFixedCostTemplate
                                                .productFixedCostTemplateItemSet
                                                ? product
                                                      .productFixedCostTemplate
                                                      .productFixedCostTemplateItemSet
                                                      .edges
                                                : []
                                        "
                                        :loading="
                                            $apollo.queries.product.loading ||
                                                isLoadingTenantProductFixedCostTemplates
                                        "
                                        item-key="node.id"
                                        disable-pagination
                                        hide-default-footer
                                        class="pa-3"
                                    >
                                        <template
                                            v-slot:item.node.costValue="{
                                                item
                                            }"
                                        >
                                            €{{ item.node.costValue }}
                                        </template>

                                        <!-- table footer-->
                                        <template slot="body.append">
                                            <!-- subtotal -->
                                            <tr>
                                                <th class="subtitle-2">
                                                    Total
                                                </th>
                                                <th
                                                    class="subtitle-2 text-center"
                                                >
                                                    €{{ productTotalCost }}
                                                </th>
                                            </tr>
                                        </template>
                                    </v-data-table>
                                </v-col>
                            </v-row>
                        </v-card>
                    </template>
                </v-hover>
            </v-col>

            <!-- Product Items -->
            <v-col cols="12" sm="7" md="8" lg="9" class="px-1">
                <v-card
                    flat
                    class="grey lighten-5"
                    :loading="$apollo.queries.product.loading"
                >
                    <!-- title -->
                    <v-card-title>
                        <v-icon class="mr-1" color="primary">eco</v-icon>
                        {{ isEditingProductItem ? "Select " : ""
                        }}{{ product.isDynamic ? "Optional Plants" : "Plants" }}
                        <v-spacer></v-spacer>
                        <span>
                            <v-row>
                                <v-btn
                                    text
                                    rounded
                                    v-if="isEditingProductItem"
                                    @click="onCancelEditProductItems"
                                    >Cancel</v-btn
                                >
                                <v-btn
                                    color="primary"
                                    outlined
                                    rounded
                                    class="mr-3"
                                    v-if="isEditingProductItem"
                                    @click="onSaveProductItems"
                                    :loading="isSavingProductItem"
                                    >Save</v-btn
                                >
                            </v-row>
                        </span>
                        <v-btn
                            color="primary"
                            dark
                            outlined
                            rounded
                            small
                            v-if="
                                hasPermission('change_product') &&
                                    !isEditingProductItem &&
                                    product.productItemSet.edges.length > 0
                            "
                            @click="onStartEditProductItems"
                            >SELECT PLANTS</v-btn
                        >
                    </v-card-title>

                    <v-alert
                        v-if="
                            product.isDynamic &&
                                productItemNumPlantsCount <
                                    product.numMixPlantsPerUnitMax
                        "
                        dense
                        outlined
                        type="error"
                        class="mx-5"
                    >
                        Select at least
                        <strong>{{ product.numMixPlantsPerUnitMax }}</strong>
                        optional plants!
                    </v-alert>

                    <v-alert
                        v-if="
                            product.isDynamic &&
                                distinctedPlantColors.length <
                                    product.numColorsPerUnitMax
                        "
                        dense
                        outlined
                        type="error"
                        class="mx-5"
                    >
                        Select at least
                        <strong>{{ product.numColorsPerUnitMax }}</strong>
                        different colors!
                    </v-alert>

                    <!-- Product Items -->
                    <v-card-text v-if="!isEditingProductItem">
                        <v-row>
                            <v-col
                                cols="12"
                                v-if="product.productItemSet.edges.length == 0"
                            >
                                <v-row class="justify-center">
                                    <v-btn
                                        v-if="hasPermission('change_product')"
                                        color="primary"
                                        outlined
                                        rounded
                                        :disabled="
                                            $apollo.queries.product.loading
                                        "
                                        @click="isEditingProductItem = true"
                                        >Select Plants</v-btn
                                    >
                                </v-row>
                            </v-col>
                            <v-col
                                v-for="(item, index) in product.productItemSet
                                    .edges"
                                :key="index"
                                cols="12"
                                class="px-3 pb-1"
                            >
                                <v-hover>
                                    <template v-slot="{ hover }">
                                        <v-card
                                            :elevation="hover ? 6 : 1"
                                            class="px-3"
                                            width="100%"
                                            outlined
                                            height="100%"
                                        >
                                            <!-- title -->
                                            <div>
                                                <v-row
                                                    class="align-center justify-space-between"
                                                >
                                                    <v-col
                                                        cols="12"
                                                        md="4"
                                                        class="pa-0"
                                                    >
                                                        <v-list-item
                                                            class="pr-1"
                                                        >
                                                            <v-list-item-action
                                                                class="mr-2"
                                                            >
                                                                <v-chip
                                                                    v-if="
                                                                        product.isDynamic
                                                                    "
                                                                    outlined
                                                                    class="grey--text text--darken-2 mr-1"
                                                                    >P{{
                                                                        item
                                                                            .node
                                                                            .priority
                                                                    }}</v-chip
                                                                >
                                                                <v-icon
                                                                    v-else
                                                                    class="grey--text"
                                                                    >eco</v-icon
                                                                >
                                                            </v-list-item-action>
                                                            <v-list-item-content
                                                                class="px-0"
                                                            >
                                                                <v-list-item-title
                                                                    >{{
                                                                        item
                                                                            .node
                                                                            .plant
                                                                            .name
                                                                    }}</v-list-item-title
                                                                >
                                                                <v-list-item-subtitle
                                                                    >{{
                                                                        item
                                                                            .node
                                                                            .availableSalesInventory
                                                                    }}
                                                                    available</v-list-item-subtitle
                                                                >
                                                            </v-list-item-content>
                                                            <v-list-item-action
                                                                class="hidden-md-and-up"
                                                            >
                                                                <div
                                                                    class="hidden-md-and-up mr-2"
                                                                >
                                                                    <span
                                                                        class="title font-weight-light mr-2"
                                                                        >x</span
                                                                    >
                                                                    <span
                                                                        class="title font-weight-regular"
                                                                        >{{
                                                                            item
                                                                                .node
                                                                                .numPlants
                                                                        }}</span
                                                                    >
                                                                </div>
                                                            </v-list-item-action>
                                                        </v-list-item>
                                                    </v-col>

                                                    <v-col
                                                        cols="12"
                                                        md="5"
                                                        class="pa-1"
                                                    >
                                                        <v-row
                                                            class="align-center justify-center my-0 mx-1"
                                                        >
                                                            <v-chip
                                                                class="mr-1 my-1 primary"
                                                            >
                                                                <v-avatar left>
                                                                    <v-icon
                                                                        dark
                                                                        small
                                                                        >straighten</v-icon
                                                                    >
                                                                </v-avatar>
                                                                {{
                                                                    item.node
                                                                        .potSize
                                                                        ? item
                                                                              .node
                                                                              .potSize
                                                                              .potSizeCm +
                                                                          "cm"
                                                                        : "*"
                                                                }}
                                                            </v-chip>
                                                            <v-chip
                                                                class="mr-1 my-1 primary"
                                                            >
                                                                <v-avatar left>
                                                                    <v-icon
                                                                        dark
                                                                        small
                                                                        >color_lens</v-icon
                                                                    >
                                                                </v-avatar>
                                                                {{
                                                                    item.node
                                                                        .color
                                                                        ? item
                                                                              .node
                                                                              .color
                                                                              .color
                                                                        : "*"
                                                                }}
                                                            </v-chip>
                                                            <v-chip
                                                                class="mr-1 my-1 primary"
                                                            >
                                                                <v-avatar left>
                                                                    <v-icon
                                                                        dark
                                                                        small
                                                                        >blur_on</v-icon
                                                                    >
                                                                </v-avatar>
                                                                {{
                                                                    item.node
                                                                        .environment
                                                                        ? item
                                                                              .node
                                                                              .environment
                                                                              .environment
                                                                        : "*"
                                                                }}
                                                            </v-chip>
                                                            <v-chip
                                                                class="mr-1 my-1 primary"
                                                            >
                                                                <v-avatar left>
                                                                    <v-icon
                                                                        dark
                                                                        small
                                                                        >turned_in_not</v-icon
                                                                    >
                                                                </v-avatar>
                                                                {{
                                                                    item.node
                                                                        .tag
                                                                        ? item
                                                                              .node
                                                                              .tag
                                                                              .tag
                                                                        : "*"
                                                                }}
                                                            </v-chip>
                                                        </v-row>
                                                    </v-col>

                                                    <v-col
                                                        cols="12"
                                                        md="3"
                                                        class="hidden-sm-and-down py-0"
                                                    >
                                                        <v-row>
                                                            <v-spacer></v-spacer>
                                                            <div>
                                                                <span
                                                                    class="title font-weight-light mr-2"
                                                                    >x</span
                                                                >
                                                                <span
                                                                    class="title font-weight-regular mr-3"
                                                                    >{{
                                                                        item
                                                                            .node
                                                                            .numPlants
                                                                    }}</span
                                                                >
                                                            </div>
                                                            <v-btn
                                                                icon
                                                                color="primary"
                                                                class="mr-3"
                                                                @click="
                                                                    goToPlantDetail(
                                                                        item
                                                                            .node
                                                                            .plant
                                                                            .id
                                                                    )
                                                                "
                                                            >
                                                                <v-icon
                                                                    >launch</v-icon
                                                                >
                                                            </v-btn>
                                                        </v-row>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </v-card>
                                    </template>
                                </v-hover>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <!-- Item Editor -->
                    <ProductItemEditor
                        ref="productItemEditorRef"
                        v-if="isEditingProductItem"
                        :productNodeId="product.id"
                        :initialSelected="product.productItemSet.edges"
                    ></ProductItemEditor>
                </v-card>

                <v-card flat class="grey lighten-5">
                    <!-- title -->
                    <v-card-title>
                        <v-icon class="mr-1" color="primary"
                            >trending_up</v-icon
                        >
                        Sales Price
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }"
                                ><v-icon
                                    class="ml-1"
                                    small
                                    color="grey"
                                    v-on="on"
                                    >error_outline</v-icon
                                >
                            </template>
                            <span
                                >Average item sales quantity and price by week,
                                calculated base on last 3 years' orders</span
                            >
                        </v-tooltip>
                        <v-spacer></v-spacer>
                    </v-card-title>

                    <v-card-text>
                        <!-- product sales price chart -->
                        <ProductSalesPriceChart ref="productSalesPriceChartRef">
                        </ProductSalesPriceChart>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>


<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import ProductInfoCard from "@/components/products/ProductInfoCard.vue";
import ProductEditDialog from "@/components/products/ProductEditDialog.vue";
import DeleteDialog from "@/components/base/DeleteDialog.vue";
import ProductItemEditor from "@/components/products/ProductItemEditor.vue";
import ProductFixedCostTemplateEditDialog from "@/components/cost/ProductFixedCostTemplateEditDialog.vue";
import ProductSalesPriceChart from "@/components/products/ProductSalesPriceChart.vue";

export default {
    name: "product-detail-component",
    props: ["productNodeId"],
    components: {
        ProductInfoCard,
        ProductEditDialog,
        DeleteDialog,
        ProductItemEditor,
        ProductSalesPriceChart,
        ProductFixedCostTemplateEditDialog
    },

    apollo: {
        product: {
            query: gql`
                query product($id: ID!) {
                    product(id: $id) {
                        id
                        name
                        externalId
                        externalCode
                        externalPotsize
                        externalHeight
                        externalCover
                        externalLabel
                        externalSticker
                        isPlantsConfigured
                        isDynamic
                        isPlantsConfigured
                        numMixPlantsPerUnitMin
                        numMixPlantsPerUnitMax
                        numColorsPerUnitMin
                        numColorsPerUnitMax
                        printPriority
                        unitCorrection
                        productCategory {
                            id
                            name
                            printPriority
                        }
                        productItemSet(orderBy: "priority") {
                            edges {
                                node {
                                    id
                                    priority
                                    numPlants
                                    availableSalesInventory
                                    plant {
                                        id
                                        name
                                    }
                                    color {
                                        id
                                        color
                                    }
                                    potSize {
                                        id
                                        potSizeCm
                                    }
                                    environment {
                                        id
                                        environment
                                    }
                                    tag {
                                        id
                                        tag
                                    }
                                }
                            }
                        }
                        productFixedCostTemplate {
                            id
                            templateName
                            totalCost
                            productFixedCostTemplateItemSet {
                                edges {
                                    node {
                                        id
                                        itemName
                                        costValue
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {
                return {
                    id: this.productNodeId
                };
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update(data) {
                //send data to parent component
                this.$emit("item-fetched", data.product);

                if (!data.product) {
                    alert("Unknown or inactive product!");
                }

                if (data.product.productFixedCostTemplate) {
                    this.productFixedCostTemplateNodeId =
                        data.product.productFixedCostTemplate.id;
                }

                //return result to current componet
                return data.product;
                // return JSON.parse(JSON.stringify(data.product));
            },
            pollInterval: 60000 // ms
        },
        tenantProductFixedCostTemplates: {
            query: gql`
                query tenantProductFixedCostTemplates {
                    tenantProductFixedCostTemplates {
                        edges {
                            node {
                                id
                                templateName
                                totalCost
                                productFixedCostTemplateItemSet(
                                    orderBy: "itemName"
                                ) {
                                    edges {
                                        node {
                                            id
                                            itemName
                                            costValue
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {
                return {};
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => {
                return data.tenantProductFixedCostTemplates.edges.sort(
                    (a, b) => {
                        return a.node.templateName.toLowerCase() ==
                            b.node.templateName.toLowerCase()
                            ? 0
                            : a.node.templateName.toLowerCase() >
                              b.node.templateName.toLowerCase()
                            ? 1
                            : -1;
                    }
                );
            },
            skip: false
            // pollInterval: 60000 // ms
        }
    },

    data() {
        return {
            // isLoadingProduct: false,
            isEditingProductItem: false,
            isSavingProductItem: false,
            editProductDialog: false,
            deleteProductDialog: false,
            initialProductNodeId: this.productNodeId,
            editProductFixedCostTemplateDialog: false,
            isLoadingTenantProductFixedCostTemplates: false,

            productFixedCostTemplateNodeId: null,

            product: {
                id: null,
                name: "",
                isDynamic: false,
                salesPrice: null,
                currency: "EUR",
                productCategory: {
                    id: null,
                    name: ""
                },
                productItemSet: {
                    edges: []
                }
            },

            productFixedCostTemplateItemTableHeaders: [
                { text: "Cost Name", value: "node.itemName" },
                { text: "Cost Value", align: "center", value: "node.costValue" }
            ]
        };
    },
    computed: {
        productItemNumPlantsCount() {
            return this.product.productItemSet.edges
                .map(item => item.node.numPlants)
                .reduce(
                    (accumulator, currentValue) => accumulator + currentValue,
                    0
                );
        },
        distinctedPlantColors() {
            return [
                ...new Set(
                    this.product.productItemSet.edges.map(
                        item => item.node.color.id
                    )
                )
            ];
        },
        productTotalCost() {
            return this.product.productFixedCostTemplate
                ? this.product.productFixedCostTemplate.totalCost.toFixed(2)
                : 0;
        }
    },
    watch: {},
    created() {
        this.hasPermission = helper.hasPermission;

        this.$nextTick().then(() => {
            this.$refs.productSalesPriceChartRef.getProductSalesPriceByWeek(
                this.productNodeId
            );
        });
    },
    methods: {
        // initialize() {
        //     this.isLoadingProduct = true;

        //     console.log(this.productNodeId);

        //     this.$store
        //         .dispatch("products/fetchSingleProduct", this.productNodeId)
        //         .then(response => {
        //             this.product = response;

        //             this.isLoadingProduct = false;

        //             // this.editedProductVariant = null;
        //             // this.deletedProductVariant = null;
        //             // this.editedProductGrowthTemplate = null;
        //             // this.deletedProductGrowthTemplate = null;
        //         })
        //         .catch(error => {
        //             /*handle errors*/
        //             console.log(error);
        //             this.isLoadingProduct = false;
        //         })
        //         .finally();
        // },

        // product
        editProduct() {
            this.editProductDialog = true;
        },

        onStartEditProductItems() {
            this.isEditingProductItem = true;
        },

        onCancelEditProductItems() {
            this.isEditingProductItem = false;

            this.$apollo.queries.product.refresh();
        },

        onSaveProductItems() {
            this.isSavingProductItem = true;

            const selectedPlants = this.$refs.productItemEditorRef.selectedPlants.map(
                (item, index) => {
                    return {
                        plantNodeId: item.node.plant.id,
                        colorNodeId: item.node.color.id,
                        potSizeNodeId: item.node.potSize.id,
                        environmentNodeId: item.node.environment.id,
                        tagNodeId: item.node.tag ? item.node.tag.id : null,
                        numPlants: item.node.numPlants,
                        priority: index + 1
                    };
                }
            );

            const payload = {
                productNodeId: this.product.id,
                productItems: selectedPlants
            };

            this.$store
                .dispatch("products/updateProductItems", payload)
                .then(response => {
                    this.product = response;
                    this.isEditingProductItem = false;
                    this.isSavingProductItem = false;

                    this.$emit("product-items-updated");
                })
                .catch(error => {
                    this.isSavingProductItem = false;
                    this.error = error.graphQLErrors[0].message;
                });
        },

        deleteProductConfirmed() {
            if (this.product) {
                const payload = {
                    id: this.product.id
                };
                this.$store
                    .dispatch("products/deleteProduct", payload)
                    .then(response => {
                        // close delete dialog
                        this.deleteProductDialog = false;

                        this.$emit("item-deleted");

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `${response.name} is deleted!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch(error => {
                        this.error = error.graphQLErrors[0].message;
                    });
            }
        },

        goToPlantDetail(plantNodeId) {
            // this.$router.push({
            //     name: "plant-detail",
            //     params: { id: plant.id }
            // });

            let routeData = this.$router.resolve({
                name: "plant-detail",
                params: { id: plantNodeId }
            });
            window.open(routeData.href, "_blank");
        },

        linkProductFixedCostTemplate() {
            if (
                this.$apollo.queries.tenantProductFixedCostTemplates.loading ||
                this.isLoadingTenantProductFixedCostTemplates
            ) {
                return;
            }

            // ignore if plantFixedCostTemplateNodeId is empty
            if (!this.productFixedCostTemplateNodeId) {
                return;
            }
            // ignore if plantFixedCostTemplateNodeId is same as current
            if (
                this.product.productFixedCostTemplate &&
                this.product.productFixedCostTemplate.id ==
                    this.productFixedCostTemplateNodeId
            ) {
                return;
            }

            this.isLoadingTenantProductFixedCostTemplates = true;
            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation linkProductToProductFixedCostTemplate(
                            $input: LinkProductToProductFixedCostTemplateInput!
                        ) {
                            linkProductToProductFixedCostTemplate(
                                input: $input
                            ) {
                                product {
                                    id
                                    name
                                    externalId
                                    externalCode
                                    externalPotsize
                                    externalHeight
                                    externalCover
                                    externalLabel
                                    externalSticker
                                    isPlantsConfigured
                                    isDynamic
                                    isPlantsConfigured
                                    numMixPlantsPerUnitMin
                                    numMixPlantsPerUnitMax
                                    numColorsPerUnitMin
                                    numColorsPerUnitMax
                                    printPriority
                                    productCategory {
                                        id
                                        name
                                        printPriority
                                    }
                                    productItemSet(orderBy: "priority") {
                                        edges {
                                            node {
                                                id
                                                priority
                                                numPlants
                                                availableSalesInventory
                                                plant {
                                                    id
                                                    name
                                                }
                                                color {
                                                    id
                                                    color
                                                }
                                                potSize {
                                                    id
                                                    potSizeCm
                                                }
                                                environment {
                                                    id
                                                    environment
                                                }
                                                tag {
                                                    id
                                                    tag
                                                }
                                            }
                                        }
                                    }
                                    productFixedCostTemplate {
                                        id
                                        templateName
                                        totalCost
                                        productFixedCostTemplateItemSet {
                                            edges {
                                                node {
                                                    id
                                                    itemName
                                                    costValue
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            productNodeId: this.product.id,
                            productFixedCostTemplateNodeId: this
                                .productFixedCostTemplateNodeId
                        }
                    }
                })
                .then(() => {
                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Saved!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .catch(error => {
                    // Error
                    console.error(error);
                    let _error = error.graphQLErrors
                        .map(error => error.message)
                        .join(", ");
                    // show snackbar
                    const payload = {
                        color: "error",
                        message: _error
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    this.isLoadingTenantProductFixedCostTemplates = false;
                });
        },

        addProductFixedCostTemplate() {
            this.editProductFixedCostTemplateDialog = true;
        },

        onProductFixedCostTemplateCreated() {
            // refresh tenantPlantFixedCostTemplates
            this.$apollo.queries.tenantProductFixedCostTemplates.refresh();
        }
    }
};
</script>